import { z } from '@hono/zod-openapi';

/**
 * Metadata for paginated list endpoints.
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Standard successful API response envelope.
 */
export interface ApiResponse<T> {
  success: true;
  data: T;
  meta?: Record<string, unknown>;
}

/**
 * Standard paginated API response envelope.
 */
export interface PaginatedResponse<T> {
  success: true;
  data: T[];
  meta: PaginationMeta;
}

/**
 * Standard structured API error response envelope.
 */
export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
    timestamp: string;
  };
}

/**
 * Helper factory to construct standard success responses.
 */
export function createSuccessResponse<T>(data: T, meta?: Record<string, unknown>): ApiResponse<T> {
  return {
    success: true,
    data,
    ...(meta ? { meta } : {}),
  };
}

/**
 * Helper factory to construct standard paginated responses.
 */
export function createPaginatedResponse<T>(
  data: T[],
  meta: PaginationMeta,
): PaginatedResponse<T> {
  return {
    success: true,
    data,
    meta,
  };
}

/**
 * Helper factory to construct standard error responses.
 */
export function createErrorResponse(
  code: string,
  message: string,
  details?: unknown,
): ApiErrorResponse {
  return {
    success: false,
    error: {
      code,
      message,
      ...(details !== undefined ? { details } : {}),
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * Creates a generic OpenAPI-compatible success envelope Zod schema.
 */
export function createSuccessEnvelopeSchema<T extends z.ZodTypeAny>(
  dataSchema: T,
  description = 'Successful response',
) {
  return z
    .object({
      success: z.literal(true).openapi({ description: 'Indicates operation success', example: true }),
      data: dataSchema,
      meta: z.record(z.unknown()).optional().openapi({ description: 'Optional operational metadata' }),
    })
    .openapi({ description });
}

/**
 * Zod schema for pagination metadata.
 */
export const PaginationMetaSchema = z
  .object({
    page: z.number().int().openapi({ description: 'Current page number', example: 1 }),
    limit: z.number().int().openapi({ description: 'Items per page', example: 20 }),
    totalItems: z.number().int().openapi({ description: 'Total matching records', example: 150 }),
    totalPages: z.number().int().openapi({ description: 'Total calculated pages', example: 8 }),
    hasNextPage: z.boolean().openapi({ description: 'Whether next page exists', example: true }),
    hasPrevPage: z.boolean().openapi({ description: 'Whether previous page exists', example: false }),
  })
  .openapi('PaginationMeta');

/**
 * Creates a generic OpenAPI-compatible paginated envelope Zod schema.
 */
export function createPaginatedEnvelopeSchema<T extends z.ZodTypeAny>(
  itemSchema: T,
  description = 'Paginated list response',
) {
  return z
    .object({
      success: z.literal(true).openapi({ description: 'Indicates operation success', example: true }),
      data: z.array(itemSchema),
      meta: PaginationMetaSchema,
    })
    .openapi({ description });
}

/**
 * Creates a generic OpenAPI-compatible error envelope Zod schema.
 */
export const ErrorEnvelopeSchema = z
  .object({
    success: z.literal(false).openapi({ description: 'Indicates operation failure', example: false }),
    error: z.object({
      code: z.string().openapi({ description: 'Standard machine-readable error code', example: 'AUTH_UNAUTHORIZED' }),
      message: z.string().openapi({ description: 'Human-readable error explanation', example: 'Invalid credentials provided' }),
      details: z.unknown().optional().openapi({ description: 'Additional validation errors or debug details' }),
      timestamp: z.string().openapi({ description: 'ISO 8601 timestamp of error occurrence', example: '2026-10-19T12:00:00.000Z' }),
    }),
  })
  .openapi({ description: 'Standard API Error Envelope' });


